/**
 * SlashCommand - Muestra menú de opciones al escribir "/"
 * Mismas opciones que el BubbleMenu: párrafo, H1-H4, negrita, cursiva, etc.
 */

import * as React from 'react';
import { Table as TableIcon, List, ListOrdered, Info } from 'lucide-react';
import { Extension } from '@tiptap/core';
import { PluginKey } from '@tiptap/pm/state';
import { createRoot, type Root } from 'react-dom/client';
import { Suggestion } from '@tiptap/suggestion';
import type { SuggestionProps } from '@tiptap/suggestion';
import type { Editor } from '@tiptap/core';
import { SlashCommandList } from './SlashCommandList';

export interface SlashCommandItem {
  id: string;
  label: string;
  /** Contenido del botón (como en el bubble): ¶, H1, <strong>B</strong>, etc. */
  bubbleContent: React.ReactNode;
  group: number; // Para separadores entre grupos
  keywords?: string[];
  command: (editor: Editor) => void;
}

const SLASH_PLUGIN_KEY = new PluginKey('slashCommand');

const ALL_ITEMS: SlashCommandItem[] = [
  { id: 'paragraph', label: 'Párrafo', bubbleContent: '¶', group: 0, keywords: ['texto', 'p'], command: (e) => e.chain().focus().setParagraph().run() },
  { id: 'h1', label: 'Título 1', bubbleContent: 'H1', group: 0, keywords: ['h1', 'titulo'], command: (e) => e.chain().focus().toggleHeading({ level: 1 }).run() },
  { id: 'h2', label: 'Título 2', bubbleContent: 'H2', group: 0, keywords: ['h2'], command: (e) => e.chain().focus().toggleHeading({ level: 2 }).run() },
  { id: 'h3', label: 'Título 3', bubbleContent: 'H3', group: 0, keywords: ['h3'], command: (e) => e.chain().focus().toggleHeading({ level: 3 }).run() },
  { id: 'h4', label: 'Título 4', bubbleContent: 'H4', group: 0, keywords: ['h4'], command: (e) => e.chain().focus().toggleHeading({ level: 4 }).run() },
  { id: 'bold', label: 'Negrita', bubbleContent: <strong>B</strong>, group: 1, keywords: ['bold', 'negrita'], command: (e) => e.chain().focus().toggleBold().run() },
  { id: 'italic', label: 'Cursiva', bubbleContent: <em>I</em>, group: 1, keywords: ['italic', 'cursiva'], command: (e) => e.chain().focus().toggleItalic().run() },
  { id: 'underline', label: 'Subrayado', bubbleContent: <u>U</u>, group: 1, keywords: ['underline'], command: (e) => e.chain().focus().toggleUnderline().run() },
  { id: 'bulletList', label: 'Lista de viñetas', bubbleContent: <List size={16} />, group: 2, keywords: ['lista', 'bullet'], command: (e) => e.chain().focus().toggleBulletList().run() },
  { id: 'orderedList', label: 'Lista numerada', bubbleContent: <ListOrdered size={16} />, group: 2, keywords: ['numerada', 'ordered'], command: (e) => e.chain().focus().toggleOrderedList().run() },
  { id: 'infoBox', label: 'Mensaje informativo', bubbleContent: <Info size={16} />, group: 3, keywords: ['info', 'informacion', 'mensaje'], command: (e) => e.chain().focus().insertInfoBox().run() },
  { id: 'table', label: 'Insertar tabla', bubbleContent: <TableIcon size={16} />, group: 4, keywords: ['tabla', 'table'], command: (e) => e.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run() },
];

const SLASH_ITEMS = ALL_ITEMS.filter(
  (item) =>
    item.id === 'table' ||
    item.id === 'bulletList' ||
    item.id === 'orderedList' ||
    item.id === 'infoBox'
);

function filterItems(query: string): SlashCommandItem[] {
  const q = query.toLowerCase().trim();
  if (!q) return SLASH_ITEMS;
  return SLASH_ITEMS.filter(
    (item) =>
      item.label.toLowerCase().includes(q) ||
      item.keywords?.some((k) => k.includes(q))
  );
}

interface SlashCommandProps extends SuggestionProps<SlashCommandItem> {
  selectedIndex?: number;
}

export const SlashCommandExtension = Extension.create({
  name: 'slashCommand',

  addProseMirrorPlugins() {
    const editor = this.editor;
    let root: Root | null = null;
    let container: HTMLDivElement | null = null;
    let currentProps: SlashCommandProps | null = null;
    let selectedIndex = 0;

    const renderList = (props: SlashCommandProps) => {
      if (props.items.length === 0) {
        unmountList();
        return;
      }
      if (!container) {
        container = document.createElement('div');
        document.body.appendChild(container);
        root = createRoot(container);
      }
      currentProps = props;
      root!.render(
        <SlashCommandList
          items={props.items}
          selectedIndex={props.selectedIndex ?? 0}
          onSelect={(item) => {
            props.command(item);
            unmountList();
          }}
          onItemHover={(index) => {
            selectedIndex = index;
            if (currentProps) renderList({ ...currentProps, selectedIndex });
          }}
          clientRect={props.clientRect ?? null}
          decorationNode={props.decorationNode}
        />
      );
    };

    const unmountList = () => {
      if (root && container) {
        root.render(null);
        container.remove();
        root = null;
        container = null;
      }
      currentProps = null;
    };

    return [
      Suggestion<SlashCommandItem, SlashCommandItem>({
        pluginKey: SLASH_PLUGIN_KEY,
        editor,
        char: '/',
        allowSpaces: true,
        allowedPrefixes: [' ', '\n'],
        startOfLine: false,
        decorationTag: 'span',
        decorationClass: 'slash-command-trigger',
        items: ({ query }) => filterItems(query),
        command: ({ editor: e, range, props }) => {
          e.chain().focus().deleteRange(range).run();
          props.command(e);
        },
        render: () => ({
          onStart: (props) => {
            selectedIndex = 0;
            renderList({ ...props, selectedIndex: 0 });
          },
          onUpdate: (props) => {
            selectedIndex = Math.min(selectedIndex, Math.max(0, props.items.length - 1));
            renderList({ ...props, selectedIndex });
          },
          onExit: () => unmountList(),
          onKeyDown: ({ event }) => {
            if (!currentProps || currentProps.items.length === 0) return false;
            if (event.key === 'ArrowDown') {
              event.preventDefault();
              selectedIndex = Math.min(selectedIndex + 1, currentProps.items.length - 1);
              renderList({ ...currentProps, selectedIndex });
              return true;
            }
            if (event.key === 'ArrowUp') {
              event.preventDefault();
              selectedIndex = Math.max(selectedIndex - 1, 0);
              renderList({ ...currentProps, selectedIndex });
              return true;
            }
            if (event.key === 'Enter') {
              event.preventDefault();
              const item = currentProps.items[selectedIndex];
              if (item) {
                currentProps.command(item);
                unmountList();
              }
              return true;
            }
            return false;
          },
        }),
      }),
    ];
  },
});
